import type { UserRole } from '@faktorplus/shared';

export interface NavItem {
  to: string;
  label: string;
  /**
   * Shown instead of `label` on a phone, where the top bar has ~390px for the
   * whole menu. Only set it where the shorter wording still names the same
   * thing — a menu that reads differently on two devices is worse than one
   * that wraps.
   */
  shortLabel?: string;
  /** Which dashboard counter, if any, is shown as a badge next to the label. */
  badge?: 'awaitingMe' | 'toAssign';
}

/**
 * Navigation is per role, not one menu with hidden items.
 *
 * Each role's first entry is its queue — the answer to "what is waiting for
 * me" — because that is the only screen most users need on most days.
 */
const NAVIGATION: Record<UserRole, NavItem[]> = {
  SITE_MANAGER: [
    { to: '/', label: 'Do akceptacji', badge: 'awaitingMe' },
    { to: '/faktury', label: 'Moje faktury' },
    { to: '/budowy', label: 'Budowy' },
    { to: '/zamowienia', label: 'Zamówienia' },
    // The address book is everybody's (24.09.2026): the manager meeting a new
    // crew is exactly the person who has their number.
    { to: '/kontakty', label: 'Kontakty' },
  ],
  ACCOUNTANT: [
    { to: '/', label: 'Do przypisania', badge: 'toAssign' },
    // Releasing for payment in the owner's place — allowed since 3.09.2026,
    // every such release is marked on the invoice for good.
    { to: '/do-zatwierdzenia', label: 'Do zatwierdzenia', badge: 'awaitingMe' },
    { to: '/faktury', label: 'Rejestr faktur' },
    { to: '/budowy', label: 'Budowy' },
    { to: '/rozrachunki', label: 'Rozrachunki' },
    { to: '/zamowienia', label: 'Zamówienia' },
    { to: '/kontakty', label: 'Kontakty' },
    { to: '/ustawienia', label: 'Ustawienia' },
  ],
  ADMIN: [
    { to: '/', label: 'Pulpit' },
    { to: '/do-zatwierdzenia', label: 'Do zatwierdzenia', badge: 'awaitingMe' },
    { to: '/faktury', label: 'Rejestr faktur' },
    { to: '/budowy', label: 'Budowy' },
    { to: '/rozrachunki', label: 'Rozrachunki' },
    { to: '/zamowienia', label: 'Zamówienia' },
    { to: '/kontakty', label: 'Kontakty' },
    { to: '/ustawienia', label: 'Ustawienia' },
  ],
};

export function navigationFor(role: UserRole): NavItem[] {
  return NAVIGATION[role];
}
